import { useState } from "react";
import { toast } from "sonner";
import { SectionHead, LevelPill, nextLevel, LEVEL_META } from "./shared";

export function PermissionMatrix({ data, api, onChanged }) {
  const [pending, setPending] = useState(null);

  const cycle = async (role, moduleKey) => {
    const level = nextLevel(role.permissions[moduleKey]);
    const id = `${role.key}:${moduleKey}`;
    setPending(id);
    try {
      await api.put(`/admin/pioneers/roles/${role.key}/matrix`, { module: moduleKey, level });
      toast.success(`${role.label} · ${data.modules.find((m) => m.key === moduleKey)?.label} → ${LEVEL_META[level].label}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update cell"); }
    finally { setPending(null); }
  };

  return (
    <section className="admin-card" data-testid="pioneer-permission-matrix">
      <SectionHead kicker="Permission matrix" title="Sub-role × Module"
        action={<div className="flex items-center gap-3 text-xs text-[#888888]">
          <span className="flex items-center gap-1.5"><LevelPill level="full" /> Full</span>
          <span className="flex items-center gap-1.5"><LevelPill level="read" /> Read only</span>
          <span className="flex items-center gap-1.5"><LevelPill level="none" /> None</span>
          <span className="hidden md:inline">· click any cell to cycle</span>
        </div>} />
      <div className="overflow-x-auto admin-scroll">
        <table className="min-w-[900px] w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky left-0 bg-[#1A1A1A] z-10 text-left pb-3 pr-4 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9A84C]">Module</th>
              {data.roles.map((r) => (
                <th key={r.key} className="pb-3 px-2 text-center font-grotesk text-[10px] uppercase tracking-[0.12em] text-[#F5F5F5] whitespace-nowrap" data-testid={`matrix-col-${r.key}`}>{r.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.modules.map((mod) => (
              <tr key={mod.key} className="group" data-testid={`matrix-row-${mod.key}`}>
                <td className="sticky left-0 bg-[#1A1A1A] group-hover:bg-[#202020] z-10 py-2 pr-4 text-sm text-[#F5F5F5] border-t border-[#262626] whitespace-nowrap">{mod.label}</td>
                {data.roles.map((r) => (
                  <td key={r.key} className="py-2 px-2 text-center border-t border-[#262626] group-hover:bg-[#202020]">
                    <LevelPill level={r.permissions[mod.key]} testid={`matrix-cell-${r.key}-${mod.key}`} onClick={() => cycle(r, mod.key)} disabled={pending === `${r.key}:${mod.key}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-[#888888]">Changes apply in real time to every member holding the sub-role, except named individuals with an override on that module. Platform Admin and Dinesh Singh are never governed by this matrix.</p>
    </section>
  );
}
